import React, { useEffect, useState } from 'react';
import GoogleLoginButtonNew from 'Components/Auth/GoogleLoginButtonNew';
import {
    Box,
    Button,
    Card,
    Container,
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Typography,
    useTheme
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { useMutation } from '@apollo/client';
import { VERIFY_EMAIL } from 'schemas/mutation';
import MicrosoftLoginButton from 'Components/Auth/MicrosoftLogin';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { useNavigate } from 'react-router';
import ConsentOtp from 'Components/ExtensionAuth/auth/consent/otp';
import { FingerPrintHash } from 'utils/extension';
import { useDispatch, useSelector } from 'react-redux';
import { SET_TOAST_OBJECT } from 'store/actions';

const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${process.env.REACT_APP_MICROSOFT_TENANT_ID}`,
        redirectUri: 'https://stage.diolog.com.au/sign-in-extension'
    }
};

const SignInCard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const msalInstance = new PublicClientApplication(msalConfig);
    const theme = useTheme();
    const [verifyEmail] = useMutation(VERIFY_EMAIL);
    const [signIn, setSignIn] = useState(true);
    const [data, setData] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const senderName = useSelector((state) => state.customization?.userData?.userName);

    useEffect(() => {
        window.addEventListener('popstate', function () {
            navigate('/dashboard/company-profile/integrate-diolog/email-extension', { replace: true });
        });
    }, []);

    const handleChangePage = (data) => {
        onSubmit({ email: data.email });
    };

    const handleMicrosoftLogin = (data) => {
        const username = data.account.name.split(' ');
        onSubmit({ email: data.account.username, firstName: username[0], lastName: username[1], id: data.account.homeAccountId });
    };

    const onSubmit = async (values) => {
        try {
            const fingerHash = await FingerPrintHash();
            setIsLoading(true);
            const response = await verifyEmail({ variables: { body: { email: values.email, senderName, fingerprint: fingerHash } } });

            const obj = {
                status: true,
                variant: 'success',
                message: response.data.verifyExtensionEmail.message
            };

            setData(values);
            setIsLoading(false);
            dispatch({ type: SET_TOAST_OBJECT, toastObject: obj });
            navigate(-1);
        } catch (error) {
            setIsLoading(false);
            setError(error.message);
        }
    };

    return signIn ? (
        <main className={isLoading ? 'auth-container loading' : ''} style={{ height: '100vh', width: '100vw' }}>
            <Container sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100vw' }}>
                <Card
                    sx={{
                        border: '1px solid #DEDEDF',
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'min-content',
                        background: 'white',
                        gap: 3,
                        width: 375
                    }}
                >
                    <>
                        <Typography className="font-roboto" variant="body1" fontSize="0.875rem" color="#00214D" fontWeight={500}>
                            Authorise any email for Diolog to access email information
                        </Typography>
                        <GoogleLoginButtonNew
                            handleChangePage={() => {}}
                            width={326}
                            updateLoading={() => setIsLoading(true)}
                            handleFailure={() => setIsLoading(false)}
                            handleSuccess={handleChangePage}
                        />
                        <MsalProvider instance={msalInstance}>
                            <MicrosoftLoginButton handleSuccess={handleMicrosoftLogin} />
                        </MsalProvider>
                        <Divider sx={{ width: '100%', color: '#00214D', fontSize: 16, fontWeight: 500 }}>Or</Divider>
                        <Typography className="font-roboto">Connect via email address</Typography>
                        <Formik
                            initialValues={{
                                email: ''
                            }}
                            validationSchema={Yup.object().shape({
                                email: Yup.string()
                                    .email('Sorry, but this email address is invalid.')
                                    .max(255)
                                    .required('Please fill out your email address.')
                            })}
                            onSubmit={onSubmit}
                        >
                            {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
                                <form noValidate onSubmit={handleSubmit} style={{ width: '100%' }}>
                                    <FormControl
                                        fullWidth
                                        error={Boolean(touched.email && errors.email)}
                                        sx={{ ...theme.typography.customInput }}
                                        className="mt-0 mb-3"
                                    >
                                        <InputLabel htmlFor="outlined-adornment-email-login">Email address</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-email-login"
                                            type="email"
                                            value={values.email}
                                            name="email"
                                            onBlur={handleBlur}
                                            onChange={(text) => {
                                                handleChange(text);
                                                if (error) {
                                                    setError(null);
                                                }
                                            }}
                                            label="Email address"
                                            inputProps={{}}
                                            autoComplete="username"
                                        />
                                        {touched.email && errors.email && (
                                            <FormHelperText error id="standard-weight-helper-text-email-login">
                                                {errors.email}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                    {error && error && (
                                        <FormHelperText error id="standard-weight-helper-text-email-login">
                                            {error}
                                        </FormHelperText>
                                    )}
                                    <Box sx={{ mt: 3 }}>
                                        <AnimateButton>
                                            <Button
                                                disableElevation
                                                disableRipple
                                                fullWidth
                                                size="large"
                                                type="submit"
                                                variant="contained"
                                                className="alternate"
                                            >
                                                Connect with email
                                            </Button>
                                        </AnimateButton>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </>
                </Card>
            </Container>
        </main>
    ) : (
        <ConsentOtp userEmail={data.email} />
    );
};

export default SignInCard;
